import React from "react";
import { Box, Button, ButtonGroup, Paper, Stack, Typography } from "@mui/material";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const timeframeOptions = [3, 6, 12];

const TimeSeriesCard = ({
  data = [],
  formatter = (value) => value,
  onTimeframeChange,
  subtitle,
  timeframe = 6,
  title,
}) => {
  const total = data.reduce((sum, item) => sum + Number(item.value || 0), 0);
  const hasTrendData = data.some((item) => Number(item.value || 0) > 0);

  return (
    <Paper variant="outlined" sx={{ borderColor: "#dadce0", borderRadius: 2, p: 2, height: "100%" }}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} sx={{ mb: 1.5 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            {title}
          </Typography>
          {!!subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        {!!onTimeframeChange && (
          <ButtonGroup size="small" variant="outlined" aria-label="Chart timeframe">
            {timeframeOptions.map((option) => (
              <Button
                key={option}
                variant={Number(timeframe) === option ? "contained" : "outlined"}
                onClick={() => onTimeframeChange(option)}
                sx={{ textTransform: "none", fontWeight: 800, minWidth: 44 }}
              >
                {option}M
              </Button>
            ))}
          </ButtonGroup>
        )}
      </Stack>

      {data.length === 0 ? (
        <Box sx={{ alignItems: "center", display: "flex", minHeight: 220 }}>
          <Typography variant="body2" color="text.secondary">
            No trend data available yet.
          </Typography>
        </Box>
      ) : (
        <>
          <Typography variant="h5" sx={{ fontWeight: 850, mb: 1 }}>
            {formatter(total)}
          </Typography>
          {hasTrendData ? (
            <Box sx={{ width: "100%", height: 240 }}>
              <ResponsiveContainer>
                <AreaChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="TrendFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#1a73e8" stopOpacity={0.24} />
                      <stop offset="95%" stopColor="#1a73e8" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#edf0f3" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#6b7280" }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fontSize: 12, fill: "#6b7280" }} tickLine={false} axisLine={false} tickFormatter={(value) => formatter(value)} width={72} />
                  <Tooltip
                    formatter={(value) => formatter(value)}
                    labelStyle={{ fontWeight: 800, color: "#30343b" }}
                    contentStyle={{ borderRadius: 8, borderColor: "#dadce0" }}
                  />
                  <Area type="monotone" dataKey="value" stroke="#1a73e8" strokeWidth={3} fill="url(#TrendFill)" dot={{ r: 3 }} activeDot={{ r: 5 }} />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          ) : (
            <Box
              sx={{
                alignItems: "center",
                bgcolor: "#f8fafc",
                border: "1px dashed #dadce0",
                borderRadius: 2,
                display: "flex",
                minHeight: 220,
                px: 2,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No activity recorded for this timeframe yet.
              </Typography>
            </Box>
          )}
        </>
      )}
    </Paper>
  );
};

export default TimeSeriesCard;
